// Image picker for the article editor. Upload mode crops (react-easy-crop),
// downscales and re-encodes to WebP in the browser before a direct upload.
// The full-size original is uploaded alongside the crop and remembered on the
// image node (data-original-src) so the crop can be redone later. URL mode
// embeds an external image. window.prompt is banned by CLAUDE.md.

import { useEffect, useRef, useState } from 'react'
import Cropper, { type Area } from 'react-easy-crop'
import { Crop, Upload } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/Dialog'
import { cn } from '@/lib/utils'
import { processImage, uploadImage } from '@/lib/imageProcessing'

export interface ImageAttrs {
  src: string
  alt: string
  width: number | null
  originalSrc?: string | null
}

interface ImageDialogProps {
  open: boolean
  initial: ImageAttrs | null
  onOpenChange: (open: boolean) => void
  onSubmit: (attrs: ImageAttrs) => void
}

const ASPECTS = [
  { label: 'Original', value: 0 },
  { label: '16:9', value: 16 / 9 },
  { label: '4:3', value: 4 / 3 },
  { label: '1:1', value: 1 },
]

export function ImageDialog({ open, initial, onOpenChange, onSubmit }: ImageDialogProps) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [tab, setTab] = useState('upload')
  const [file, setFile] = useState<File | null>(null)
  // What the cropper shows: an object URL for a new file, or the stored
  // original when re-cropping an existing upload.
  const [cropSource, setCropSource] = useState<string | null>(null)
  const [natural, setNatural] = useState({ width: 16, height: 9 })
  const [aspect, setAspect] = useState(0)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [cropPixels, setCropPixels] = useState<Area | null>(null)
  const [src, setSrc] = useState('')
  const [alt, setAlt] = useState('')
  const [width, setWidth] = useState('')
  const [busy, setBusy] = useState(false)
  const editing = Boolean(initial?.src)

  useEffect(() => {
    if (!open) return
    setTab(initial?.src ? 'url' : 'upload')
    setSrc(initial?.src ?? '')
    setAlt(initial?.alt ?? '')
    setWidth(initial?.width ? String(initial.width) : '')
    setFile(null)
    setCropSource(null)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCropPixels(null)
    setBusy(false)
  }, [open, initial])

  const probeSource = (url: string) => {
    const probe = new Image()
    probe.onload = () => setNatural({ width: probe.naturalWidth, height: probe.naturalHeight })
    probe.src = url
  }

  const pickFile = (picked: File) => {
    const url = URL.createObjectURL(picked)
    probeSource(url)
    setFile(picked)
    setCropSource(url)
    setTab('upload')
  }

  const startRecrop = () => {
    if (!initial?.originalSrc) return
    probeSource(initial.originalSrc)
    setFile(null)
    setCropSource(initial.originalSrc)
    setTab('upload')
  }

  const parsedWidth = () => {
    const value = parseInt(width, 10)
    return Number.isFinite(value) && value > 0 ? value : null
  }

  const isFullFrame = () =>
    !cropPixels ||
    (Math.abs(cropPixels.width - natural.width) < 2 && Math.abs(cropPixels.height - natural.height) < 2)

  const submit = async () => {
    if (tab === 'url' && !cropSource) {
      if (!src.trim()) return
      const keepOriginal = src.trim() === initial?.src ? initial?.originalSrc ?? null : null
      onSubmit({ src: src.trim(), alt: alt.trim(), width: parsedWidth(), originalSrc: keepOriginal })
      onOpenChange(false)
      return
    }
    if (!cropSource) return
    setBusy(true)
    try {
      const source = file ?? cropSource
      const cropped = await processImage(source, isFullFrame() ? null : cropPixels)
      const croppedUrl = await uploadImage(cropped)
      let originalSrc = initial?.originalSrc ?? null
      if (file) {
        // New upload: also store the uncropped original (skip the second
        // upload when the crop is the full frame — they'd be identical).
        originalSrc = isFullFrame() ? croppedUrl : await uploadImage(await processImage(file, null))
      }
      onSubmit({ src: croppedUrl, alt: alt.trim(), width: parsedWidth() ?? cropped.width, originalSrc })
      onOpenChange(false)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit image' : 'Insert image'}</DialogTitle>
          <DialogDescription>Uploads are cropped, resized and converted to WebP in your browser.</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="upload" className="cursor-pointer">Upload</TabsTrigger>
            <TabsTrigger value="url" className="cursor-pointer">URL</TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="space-y-3 mt-3">
            {!cropSource ? (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="flex h-40 w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border text-sm text-muted-foreground hover:bg-muted/40"
              >
                <Upload className="size-5" /> Choose an image…
              </button>
            ) : (
              <>
                <div className="relative h-64 w-full overflow-hidden rounded-md bg-muted">
                  <Cropper
                    image={cropSource}
                    crop={crop}
                    zoom={zoom}
                    aspect={aspect === 0 ? natural.width / natural.height : aspect}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={(_area, pixels) => setCropPixels(pixels)}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {ASPECTS.map(option => (
                    <Button
                      key={option.label}
                      type="button"
                      size="sm"
                      variant={aspect === option.value ? 'secondary' : 'ghost'}
                      className={cn('h-7 px-2 text-xs', aspect === option.value && 'font-semibold')}
                      onClick={() => setAspect(option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                  <input
                    type="range" min={1} max={3} step={0.05} value={zoom}
                    onChange={e => setZoom(Number(e.target.value))}
                    className="ml-auto w-32 cursor-pointer" aria-label="Zoom"
                  />
                  <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => fileRef.current?.click()}>
                    Change file
                  </Button>
                </div>
              </>
            )}
            <input
              ref={fileRef} type="file" accept="image/*" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) pickFile(f) }}
            />
          </TabsContent>

          <TabsContent value="url" className="space-y-3 mt-3">
            {src.startsWith('/rails/active_storage/') ? (
              // A stored upload — show the image, not the opaque blob URL.
              <div className="space-y-3">
                <img src={src} alt={alt} className="max-h-48 w-full rounded-md border border-border object-contain" />
                <div className="flex flex-wrap gap-2">
                  {initial?.originalSrc && (
                    <Button type="button" variant="outline" size="sm" onClick={startRecrop}>
                      <Crop className="size-4" /> Re-crop from original
                    </Button>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                    Replace image
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="image-src">Image URL</Label>
                <Input id="image-src" value={src} onChange={e => setSrc(e.target.value)} placeholder="https://…" />
              </div>
            )}
          </TabsContent>
        </Tabs>

        <div className="grid grid-cols-[1fr_8rem] gap-3">
          <div className="space-y-2">
            <Label htmlFor="image-alt">Alt text</Label>
            <Input id="image-alt" value={alt} onChange={e => setAlt(e.target.value)} placeholder="What the image shows" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="image-width">Width (px)</Label>
            <Input id="image-width" type="number" min={1} value={width} onChange={e => setWidth(e.target.value)} placeholder="Auto" />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" onClick={submit} disabled={busy || (cropSource ? false : !src.trim())}>
            {busy ? 'Uploading…' : editing ? 'Update' : 'Insert'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
